import React, {
  useState,
  useEffect,
  useMemo,
  useRef,
  useCallback,
} from "react";
import { Copy, Check, X, ArrowRight, Download } from "lucide-react";
import { getMoleculeSVG, getMoleculeRender } from "../../../utils/Api";
import { useDownloadPng } from "../../../hooks/useDownloadPng";

// ─── Helpers ────────────────────────────────────────────────────────────────

const truncateSmiles = (smiles: string, maxLen: number = 24): string => {
  if (!smiles) return "";
  return smiles.length > maxLen ? smiles.substring(0, maxLen) + "..." : smiles;
};

/** Format coref text into a labelled metadata element. Label portion is faint italic. */
const formatMetadata = (
  text: string | null | undefined,
): React.ReactNode | null => {
  if (!text) return null;
  const trimmed = text.trim();
  if (!trimmed) return null;

  // Numbers followed by % → Yield
  if (/\d+\.?\d*\s*%/.test(trimmed)) {
    return (
      <>
        <span className="text-slate-400 italic">Yield: </span>
        {trimmed}
      </>
    );
  }

  // A single letter, or numbers optionally followed by a single letter → Coreference
  if (/^\d+[a-zA-Z]?$/.test(trimmed) || /^[a-zA-Z]$/.test(trimmed)) {
    return (
      <>
        <span className="text-slate-400 italic">Coreference: </span>
        {trimmed}
      </>
    );
  }

  // Anything else → plain text
  return trimmed;
};

/**
 * Spatially sort items that have a bbox [x1, y1, x2, y2].
 *
 * 1. Group items into "rows" – two items belong to the same row when their
 *    vertical (y) extents overlap by more than 50 % of the *shorter* item's
 *    height.
 * 2. Sort rows top-to-bottom (by minimum y1 in the row).
 * 3. Within each row, sort items left-to-right by x1.
 *
 * Items that share a pageIndex are sorted independently per page (page order
 * is already guaranteed by the caller).
 */
function spatialSort<T extends { bbox?: number[]; pageIndex: number }>(
  items: T[],
): T[] {
  if (items.length <= 1) return items;

  // Partition by page so spatial sort is per-page
  const byPage = new Map<number, T[]>();
  items.forEach((item) => {
    const list = byPage.get(item.pageIndex) || [];
    list.push(item);
    byPage.set(item.pageIndex, list);
  });

  const sorted: T[] = [];

  // Helper – safely read bbox coordinate (fallback 0)
  const bx = (item: T, i: number): number => {
    const b = item.bbox;
    if (!b || i >= b.length) return 0;
    return b[i] ?? 0;
  };

  // Process pages in ascending order
  Array.from(byPage.keys())
    .sort((a, b) => a - b)
    .forEach((pageIdx) => {
      const pageItems = byPage.get(pageIdx)!;
      // Items without bbox go to the end
      const withBbox: T[] = pageItems.filter(
        (i) => i.bbox && i.bbox.length >= 4,
      );
      const withoutBbox: T[] = pageItems.filter(
        (i) => !i.bbox || i.bbox.length < 4,
      );

      // Build rows by clustering items with >50 % vertical overlap
      const assigned = new Set<number>();
      const rows: T[][] = [];

      // Sort candidates by y1 first so we sweep top-to-bottom
      const candidates: T[] = withBbox
        .slice()
        .sort((a, b) => bx(a, 1) - bx(b, 1));

      for (let idx = 0; idx < candidates.length; idx++) {
        if (assigned.has(idx)) continue;
        const item = candidates[idx] as T;

        const row: T[] = [item];
        assigned.add(idx);

        // Row vertical extent (grows as we add items)
        let rowY1: number = bx(item, 1);
        let rowY2: number = bx(item, 3);

        // Try to add subsequent items to this row
        for (let j = idx + 1; j < candidates.length; j++) {
          if (assigned.has(j)) continue;
          const other = candidates[j] as T;
          const otherY1: number = bx(other, 1);
          const otherY2: number = bx(other, 3);

          // Compute vertical overlap between `other` and the current row extent
          const overlapTop: number = Math.max(rowY1, otherY1);
          const overlapBot: number = Math.min(rowY2, otherY2);
          const overlap: number = Math.max(0, overlapBot - overlapTop);

          const otherHeight: number = otherY2 - otherY1;
          const rowHeight: number = rowY2 - rowY1;
          const minHeight: number = Math.min(otherHeight, rowHeight);

          if (minHeight > 0 && overlap / minHeight > 0.5) {
            row.push(other);
            assigned.add(j);
            // Expand row extent
            rowY1 = Math.min(rowY1, otherY1);
            rowY2 = Math.max(rowY2, otherY2);
          }
        }

        rows.push(row);
      }

      // Sort rows by their minimum y1
      rows.sort((a, b) => {
        const aMinY = Math.min(...a.map((i) => bx(i, 1)));
        const bMinY = Math.min(...b.map((i) => bx(i, 1)));
        return aMinY - bMinY;
      });

      // Within each row, sort by x1
      rows.forEach((row) => {
        row.sort((a, b) => bx(a, 0) - bx(b, 0));
        sorted.push(...row);
      });

      sorted.push(...withoutBbox);
    });

  return sorted;
}

// ─── Inline molecule image (prefers DRKit graph render, falls back to CDK Depict) ─

const InlineMoleculeImage: React.FC<{
  smiles: string;
  graph?:
    | { symbols: string[]; coords: number[][]; edges: number[][] }
    | null
    | undefined;
  bboxWidth?: number | undefined;
  bboxHeight?: number | undefined;
  className?: string;
}> = ({
  smiles,
  graph,
  bboxWidth = 1,
  bboxHeight = 1,
  className = "w-20 h-16",
}) => {
  const [imageUrl, setImageUrl] = useState<string>("");
  const [error, setError] = useState(false);
  const [inView, setInView] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Only start the network request once the element is near the viewport
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const ob = new IntersectionObserver(
      ([entry]) => {
        if (entry!.isIntersecting) {
          setInView(true);
          ob.disconnect();
        }
      },
      { rootMargin: "200px" },
    );
    ob.observe(el);
    return () => ob.disconnect();
  }, []);

  useEffect(() => {
    if (!inView) return;
    let revoke = "";
    setImageUrl("");
    setError(false);
    (async () => {
      try {
        let blob: Blob;
        if (graph?.symbols?.length) {
          const response = await getMoleculeRender({
            symbols: graph.symbols,
            coords: graph.coords,
            edges: graph.edges,
            bbox_width: bboxWidth,
            bbox_height: bboxHeight,
          });
          blob = response.data as Blob;
        } else if (smiles) {
          const response = await getMoleculeSVG(smiles);
          blob = new Blob([response.data], { type: "image/svg+xml" });
        } else {
          return;
        }
        const url = URL.createObjectURL(blob);
        revoke = url;
        setImageUrl(url);
      } catch {
        setError(true);
      }
    })();
    return () => {
      if (revoke) URL.revokeObjectURL(revoke);
    };
  }, [inView, smiles, graph, bboxWidth, bboxHeight]);

  if (error || (!graph?.symbols?.length && !smiles)) {
    return (
      <div
        ref={containerRef}
        className={`${className} bg-slate-100 rounded flex items-center justify-center`}
      >
        <span className="text-[10px] text-slate-400 font-mono break-all text-center px-1">
          {smiles ? truncateSmiles(smiles, 16) : "—"}
        </span>
      </div>
    );
  }

  if (!imageUrl) {
    return (
      <div
        ref={containerRef}
        className={`${className} bg-slate-50 animate-pulse rounded`}
      />
    );
  }

  return (
    <div
      ref={containerRef}
      className={`${className} flex items-center justify-center`}
    >
      <img
        src={imageUrl}
        alt="molecule"
        className="w-full h-full object-contain"
      />
    </div>
  );
};

// ─── SMILES cell with truncation + copy ─────────────────────────────────────

const SmilesCell: React.FC<{ smiles: string; maxLen?: number }> = ({
  smiles,
  maxLen = 16,
}) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (smiles) {
      await navigator.clipboard.writeText(smiles);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    }
  };

  return (
    <div className="flex items-center gap-1 min-w-0">
      <span
        className="text-xs font-mono text-slate-700 truncate min-w-0"
        title={smiles}
      >
        {truncateSmiles(smiles, maxLen)}
      </span>
      {smiles && (
        <button
          onClick={handleCopy}
          className="p-0.5 hover:bg-slate-200 rounded transition-colors flex-shrink-0"
          title="Copy SMILES"
        >
          {copied ? (
            <Check size={12} className="text-green-600" />
          ) : (
            <Copy size={12} className="text-slate-400" />
          )}
        </button>
      )}
    </div>
  );
};

// ─── Molecule stack (used in reaction rows for reactants / products) ────────

const MoleculeStack: React.FC<{
  molecules: {
    smiles: string;
    uuid?: string;
    graph?:
      | { symbols: string[]; coords: number[][]; edges: number[][] }
      | null
      | undefined;
    bboxWidth?: number | undefined;
    bboxHeight?: number | undefined;
  }[];
}> = ({ molecules }) => {
  if (!molecules || molecules.length === 0) {
    return <span className="text-xs text-slate-400 italic">None</span>;
  }

  return (
    <div className="flex flex-col gap-2 min-w-0">
      {molecules.map((mol, idx) => (
        <div key={mol.uuid || idx} className="flex items-center gap-2 min-w-0">
          <InlineMoleculeImage
            smiles={mol.smiles}
            graph={mol.graph}
            bboxWidth={mol.bboxWidth}
            bboxHeight={mol.bboxHeight}
            className="w-16 h-12 shrink-0"
          />
        </div>
      ))}
    </div>
  );
};

// ─── Molecule detail modal ──────────────────────────────────────────────────

const MoleculeDetailModal: React.FC<{
  smiles: string;
  coref?: string | null;
  graph?:
    | { symbols: string[]; coords: number[][]; edges: number[][] }
    | null
    | undefined;
  bboxWidth?: number | undefined;
  bboxHeight?: number | undefined;
  onClose: () => void;
}> = ({ smiles, coref, graph, bboxWidth, bboxHeight, onClose }) => {
  const { containerRef, download } = useDownloadPng("molecule.png");

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-xl shadow-2xl max-w-lg w-full mx-4 overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200 bg-slate-50">
          <h3 className="text-sm font-semibold text-slate-700">
            Molecule Detail
          </h3>
          <div className="flex items-center gap-1">
            <button
              onClick={download}
              className="p-1 hover:bg-slate-200 rounded transition-colors"
              title="Download as PNG"
            >
              <Download size={16} className="text-slate-500" />
            </button>
            <button
              onClick={onClose}
              className="p-1 hover:bg-slate-200 rounded transition-colors"
            >
              <X size={16} className="text-slate-500" />
            </button>
          </div>
        </div>

        {/* Body */}
        <div
          ref={containerRef}
          className="p-6 flex flex-col items-center gap-4"
        >
          <InlineMoleculeImage
            smiles={smiles}
            graph={graph}
            bboxWidth={bboxWidth}
            bboxHeight={bboxHeight}
            className="w-64 h-48"
          />
          <div className="w-full space-y-2">
            {coref && formatMetadata(coref) && (
              <div className="flex items-start gap-2">
                <span className="text-xs font-semibold text-slate-500 w-20 shrink-0 pt-0.5">
                  Metadata
                </span>
                <span className="text-xs text-slate-700">
                  {formatMetadata(coref)}
                </span>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── Reaction detail modal ─────────────────────────────────────────────────

const ReactionDetailModal: React.FC<{
  reactants: {
    smiles: string;
    uuid?: string;
    graph?: { symbols: string[]; coords: number[][]; edges: number[][] } | null;
    bboxWidth?: number;
    bboxHeight?: number;
  }[];
  products: {
    smiles: string;
    uuid?: string;
    graph?: { symbols: string[]; coords: number[][]; edges: number[][] } | null;
    bboxWidth?: number;
    bboxHeight?: number;
  }[];
  conditions: string[];
  onClose: () => void;
}> = ({ reactants, products, conditions, onClose }) => {
  const { containerRef, download } = useDownloadPng("reaction.png", "stitch");

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-xl shadow-2xl max-w-3xl w-full mx-4 overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200 bg-slate-50">
          <h3 className="text-sm font-semibold text-slate-700">
            Reaction Detail
          </h3>
          <div className="flex items-center gap-1">
            <button
              onClick={download}
              className="p-1 hover:bg-slate-200 rounded transition-colors"
              title="Download as PNG"
            >
              <Download size={16} className="text-slate-500" />
            </button>
            <button
              onClick={onClose}
              className="p-1 hover:bg-slate-200 rounded transition-colors"
            >
              <X size={16} className="text-slate-500" />
            </button>
          </div>
        </div>

        {/* Reaction diagram */}
        <div ref={containerRef} className="p-6 overflow-x-auto">
          <div className="flex items-center justify-center gap-3 min-w-0">
            {/* Reactants */}
            {reactants.map((mol, idx) => (
              <React.Fragment key={mol.uuid || `r-${idx}`}>
                {idx > 0 && (
                  <span className="text-xl font-bold text-slate-400 shrink-0">
                    +
                  </span>
                )}
                <div className="flex flex-col items-center gap-1 shrink-0">
                  <InlineMoleculeImage
                    smiles={mol.smiles}
                    graph={mol.graph}
                    bboxWidth={mol.bboxWidth}
                    bboxHeight={mol.bboxHeight}
                    className="w-28 h-24"
                  />
                </div>
              </React.Fragment>
            ))}

            {/* Arrow + conditions */}
            <div className="flex flex-col items-center gap-1 shrink-0 mx-2">
              {conditions.length > 0 && (
                <div className="flex flex-col items-center">
                  {conditions.map((c, ci) => (
                    <span
                      key={ci}
                      className="text-[10px] text-slate-500 leading-tight"
                    >
                      {c}
                    </span>
                  ))}
                </div>
              )}
              <ArrowRight size={28} className="text-slate-600" />
            </div>

            {/* Products */}
            {products.map((mol, idx) => (
              <React.Fragment key={mol.uuid || `p-${idx}`}>
                {idx > 0 && (
                  <span className="text-xl font-bold text-slate-400 shrink-0">
                    +
                  </span>
                )}
                <div className="flex flex-col items-center gap-1 shrink-0">
                  <InlineMoleculeImage
                    smiles={mol.smiles}
                    graph={mol.graph}
                    bboxWidth={mol.bboxWidth}
                    bboxHeight={mol.bboxHeight}
                    className="w-28 h-24"
                  />
                </div>
              </React.Fragment>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── Types ──────────────────────────────────────────────────────────────────

type ChemistrySubTab = "molecules" | "reactions";

interface ChemistryDisplayProps {
  /** All pages of positioned data (same data used for the positioned tab). */
  pagesData: { pageId: string; rawData: any }[];
  /** Callback to highlight a molecule / reaction on the image panel. */
  onHoverObject?: (uuid: string | null) => void;
  /** UUID of the object currently hovered on the original page (for reverse highlight). */
  hoveredObjectUuid?: string | null;
  /** Scroll the original-document (left) panel to a given page index. */
  onScrollToPage?: (pageIndex: number) => void;
}

// ─── Main component ─────────────────────────────────────────────────────────

const ChemistryDisplay: React.FC<ChemistryDisplayProps> = ({
  pagesData,
  onHoverObject,
  hoveredObjectUuid,
  onScrollToPage,
}) => {
  const [subTab, setSubTab] = useState<ChemistrySubTab>("molecules");

  // ── Aggregate molecules across all pages ─────────────────────────────────
  const allMolecules = useMemo(() => {
    const mols: {
      smiles: string;
      uuid: string;
      coref: string | null;
      pageIndex: number;
      bbox?: number[];
      graph?: {
        symbols: string[];
        coords: number[][];
        edges: number[][];
      } | null;
      bboxWidth?: number | undefined;
      bboxHeight?: number | undefined;
    }[] = [];

    pagesData.forEach(({ rawData }, pageIndex) => {
      if (!rawData) return;

      // Build a coref lookup for this page: molecule uuid → coref text
      const corefMap = new Map<string, string>();
      (rawData.corefs || []).forEach((c: any) => {
        const molUuid =
          typeof c.molecule === "string" ? c.molecule : c.molecule?.uuid;
        if (molUuid && c.corefText) {
          corefMap.set(molUuid, c.corefText);
        }
      });

      // Standalone molecules (cls === 'molecule')
      (rawData.molecules || [])
        .filter((m: any) => m.cls === "molecule")
        .forEach((m: any) => {
          mols.push({
            smiles: m.text || "",
            uuid: m.uuid,
            coref: corefMap.get(m.uuid) ?? null,
            pageIndex,
            bbox: m.bbox,
            graph: m.graph ?? null,
            bboxWidth: m.bbox_width,
            bboxHeight: m.bbox_height,
          });
        });

      // Molecules inside reactions (reactants + products)
      (rawData.rxns || []).forEach((rxn: any) => {
        const seen = new Set<string>();
        [...(rxn.reactants || []), ...(rxn.products || [])].forEach(
          (comp: any) => {
            if (comp.uuid && !seen.has(comp.uuid)) {
              seen.add(comp.uuid);
              // Avoid duplicates with standalone list
              if (!mols.some((m) => m.uuid === comp.uuid)) {
                mols.push({
                  smiles: "",
                  uuid: comp.uuid,
                  coref: corefMap.get(comp.uuid) ?? null,
                  pageIndex,
                  bbox: comp.bbox,
                  graph: comp.graph ?? null,
                  bboxWidth: comp.bboxWidth,
                  bboxHeight: comp.bboxHeight,
                });
              }
            }
          },
        );
      });
    });

    return spatialSort(mols);
  }, [pagesData]);

  // ── Aggregate reactions across all pages ─────────────────────────────────
  const allReactions = useMemo(() => {
    const reactions: {
      uuid: string;
      reactants: { smiles: string; uuid?: string }[];
      products: { smiles: string; uuid?: string }[];
      conditions: string[];
      conditionUuids: string[];
      pageIndex: number;
      bbox?: number[];
    }[] = [];

    pagesData.forEach(({ rawData }, pageIndex) => {
      if (!rawData) return;

      // New structured format: reactions in rawData.rxns
      const rxnSources: any[] = [...(rawData.rxns || [])];

      // Legacy format: reactions sit in rawData.molecules with cls === "rxn"
      // but they lack structured reactants/products, so we still surface them
      // as empty-component rows so the tab shows something.
      if ((rawData.rxns || []).length === 0) {
        (rawData.molecules || [])
          .filter((m: any) => m.cls === "rxn")
          .forEach((m: any) => {
            if (!rxnSources.some((r) => r.uuid === m.uuid)) {
              rxnSources.push(m);
            }
          });
      }

      rxnSources.forEach((rxn: any) => {
        reactions.push({
          uuid: rxn.uuid,
          reactants: (rxn.reactants || []).map((r: any) => ({
            smiles: r.smiles || "",
            uuid: r.uuid,
            graph: r.graph || null,
            bboxWidth: r.bboxWidth,
            bboxHeight: r.bboxHeight,
          })),
          products: (rxn.products || []).map((p: any) => ({
            smiles: p.smiles || "",
            uuid: p.uuid,
            graph: p.graph || null,
            bboxWidth: p.bboxWidth,
            bboxHeight: p.bboxHeight,
          })),
          conditions: (rxn.conditions || [])
            .map((c: any) => c.resolvedText || "")
            .filter(Boolean),
          conditionUuids: (rxn.conditions || []).flatMap(
            (c: any) => c.resolvedTextUuids || [],
          ),
          pageIndex,
          bbox: rxn.bbox,
        });
      });
    });

    return spatialSort(reactions);
  }, [pagesData]);

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="flex flex-col h-full">
      {/* Sub‑tab bar */}
      <div className="flex items-center gap-1 px-4 py-2 bg-slate-50 border-b border-slate-200">
        <button
          onClick={() => setSubTab("molecules")}
          className={`px-3 py-1 text-xs font-medium rounded transition-colors ${
            subTab === "molecules"
              ? "bg-blue-600 text-white"
              : "text-slate-600 hover:bg-slate-200"
          }`}
        >
          Molecules
        </button>
        <button
          onClick={() => setSubTab("reactions")}
          className={`px-3 py-1 text-xs font-medium rounded transition-colors ${
            subTab === "reactions"
              ? "bg-blue-600 text-white"
              : "text-slate-600 hover:bg-slate-200"
          }`}
        >
          Reactions
        </button>
      </div>

      {/* Table content */}
      <div className="flex-1 overflow-auto">
        {subTab === "molecules" ? (
          <MoleculesTable
            molecules={allMolecules}
            onHoverObject={onHoverObject}
            hoveredObjectUuid={hoveredObjectUuid}
            onScrollToPage={onScrollToPage}
          />
        ) : (
          <ReactionsTable
            reactions={allReactions}
            onHoverObject={onHoverObject}
            hoveredObjectUuid={hoveredObjectUuid}
            onScrollToPage={onScrollToPage}
          />
        )}
      </div>
    </div>
  );
};

// ─── Molecules table ────────────────────────────────────────────────────────

const MoleculesTable: React.FC<{
  molecules: {
    smiles: string;
    uuid: string;
    coref: string | null;
    pageIndex: number;
    graph?:
      | { symbols: string[]; coords: number[][]; edges: number[][] }
      | null
      | undefined;
    bboxWidth?: number | undefined;
    bboxHeight?: number | undefined;
  }[];
  onHoverObject?: ((uuid: string | null) => void) | undefined;
  hoveredObjectUuid?: string | null | undefined;
  onScrollToPage?: ((pageIndex: number) => void) | undefined;
}> = ({ molecules, onHoverObject, hoveredObjectUuid, onScrollToPage }) => {
  const rowRefs = useRef<Map<string, HTMLTableRowElement>>(new Map());
  const [selectedMol, setSelectedMol] = useState<
    (typeof molecules)[number] | null
  >(null);

  const setRowRef = useCallback(
    (uuid: string, el: HTMLTableRowElement | null) => {
      if (el) rowRefs.current.set(uuid, el);
      else rowRefs.current.delete(uuid);
    },
    [],
  );

  useEffect(() => {
    if (!hoveredObjectUuid) return;
    const row = rowRefs.current.get(hoveredObjectUuid);
    if (row) {
      row.scrollIntoView({ behavior: "smooth", block: "nearest" });
    }
  }, [hoveredObjectUuid]);

  if (molecules.length === 0) {
    return (
      <div className="flex items-center justify-center h-48 text-sm text-slate-400">
        No molecules found in this document.
      </div>
    );
  }

  return (
    <>
      <table
        className="w-full text-left text-sm border-collapse"
        style={{ tableLayout: "fixed" }}
      >
        <colgroup>
          <col style={{ width: "6%" }} />
          <col style={{ width: "28%" }} />
          <col style={{ width: "56%" }} />
          <col style={{ width: "10%" }} />
        </colgroup>
        <thead className="sticky top-0 z-10 bg-slate-100 border-b border-slate-300">
          <tr>
            <th className="px-3 py-2 font-semibold text-slate-600">#</th>
            <th className="px-3 py-2 font-semibold text-slate-600">Image</th>
            <th className="px-3 py-2 font-semibold text-slate-600">Metadata</th>
            <th className="px-3 py-2 font-semibold text-slate-600">Page</th>
          </tr>
        </thead>
        <tbody>
          {molecules.map((mol, idx) => {
            const isHighlighted = !!(
              hoveredObjectUuid && mol.uuid === hoveredObjectUuid
            );
            return (
              <tr
                key={mol.uuid || `mol-${idx}`}
                ref={(el) => setRowRef(mol.uuid, el)}
                className={`border-b border-slate-200 transition-colors cursor-pointer ${
                  isHighlighted ? "bg-blue-100" : "hover:bg-blue-50"
                }`}
                onMouseEnter={() => onHoverObject?.(mol.uuid)}
                onMouseLeave={() => onHoverObject?.(null)}
                onClick={() => {
                  onScrollToPage?.(mol.pageIndex);
                  setSelectedMol(mol);
                }}
              >
                <td className="px-3 py-3 text-xs text-slate-400">{idx + 1}</td>
                <td className="px-3 py-3 overflow-hidden">
                  <InlineMoleculeImage
                    smiles={mol.smiles}
                    graph={mol.graph}
                    bboxWidth={mol.bboxWidth}
                    bboxHeight={mol.bboxHeight}
                  />
                </td>
                <td className="px-3 py-3 text-xs text-slate-600 overflow-hidden">
                  <span className="block truncate">
                    {formatMetadata(mol.coref) ?? (
                      <span className="text-slate-400 italic">None</span>
                    )}
                  </span>
                </td>
                <td className="px-3 py-3 text-xs text-slate-500">
                  {mol.pageIndex + 1}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      {selectedMol && (
        <MoleculeDetailModal
          smiles={selectedMol.smiles}
          coref={selectedMol.coref}
          graph={selectedMol.graph}
          bboxWidth={selectedMol.bboxWidth}
          bboxHeight={selectedMol.bboxHeight}
          onClose={() => setSelectedMol(null)}
        />
      )}
    </>
  );
};

// ─── Reactions table ────────────────────────────────────────────────────────

const ReactionsTable: React.FC<{
  reactions: {
    uuid: string;
    reactants: {
      smiles: string;
      uuid?: string;
      graph?: {
        symbols: string[];
        coords: number[][];
        edges: number[][];
      } | null;
      bboxWidth?: number;
      bboxHeight?: number;
    }[];
    products: {
      smiles: string;
      uuid?: string;
      graph?: {
        symbols: string[];
        coords: number[][];
        edges: number[][];
      } | null;
      bboxWidth?: number;
      bboxHeight?: number;
    }[];
    conditions: string[];
    conditionUuids: string[];
    pageIndex: number;
  }[];
  onHoverObject?: ((uuid: string | null) => void) | undefined;
  hoveredObjectUuid?: string | null | undefined;
  onScrollToPage?: ((pageIndex: number) => void) | undefined;
}> = ({ reactions, onHoverObject, hoveredObjectUuid, onScrollToPage }) => {
  const rowRefs = useRef<Map<string, HTMLTableRowElement>>(new Map());
  const [selectedRxn, setSelectedRxn] = useState<
    (typeof reactions)[number] | null
  >(null);

  const setRowRef = useCallback(
    (uuid: string, el: HTMLTableRowElement | null) => {
      if (el) rowRefs.current.set(uuid, el);
      else rowRefs.current.delete(uuid);
    },
    [],
  );

  // Build a lookup: any component uuid → reaction uuid, so we can scroll to the right row
  const componentToRxnUuid = useMemo(() => {
    const map = new Map<string, string>();
    reactions.forEach((rxn) => {
      map.set(rxn.uuid, rxn.uuid);
      rxn.reactants.forEach((r) => {
        if (r.uuid) map.set(r.uuid, rxn.uuid);
      });
      rxn.products.forEach((p) => {
        if (p.uuid) map.set(p.uuid, rxn.uuid);
      });
      rxn.conditionUuids.forEach((cu) => map.set(cu, rxn.uuid));
    });
    return map;
  }, [reactions]);

  useEffect(() => {
    if (!hoveredObjectUuid) return;
    const rxnUuid = componentToRxnUuid.get(hoveredObjectUuid);
    if (rxnUuid) {
      const row = rowRefs.current.get(rxnUuid);
      if (row) {
        row.scrollIntoView({ behavior: "smooth", block: "nearest" });
      }
    }
  }, [hoveredObjectUuid, componentToRxnUuid]);

  if (reactions.length === 0) {
    return (
      <div className="flex items-center justify-center h-48 text-sm text-slate-400">
        No reactions found in this document.
      </div>
    );
  }

  return (
    <>
      <table
        className="w-full text-left text-sm border-collapse"
        style={{ tableLayout: "fixed" }}
      >
        <colgroup>
          <col style={{ width: "6%" }} />
          <col style={{ width: "30%" }} />
          <col style={{ width: "30%" }} />
          <col style={{ width: "24%" }} />
          <col style={{ width: "10%" }} />
        </colgroup>
        <thead className="sticky top-0 z-10 bg-slate-100 border-b border-slate-300">
          <tr>
            <th className="px-3 py-2 font-semibold text-slate-600">#</th>
            <th className="px-3 py-2 font-semibold text-slate-600">
              Reactants
            </th>
            <th className="px-3 py-2 font-semibold text-slate-600">Products</th>
            <th className="px-3 py-2 font-semibold text-slate-600">
              Conditions
            </th>
            <th className="px-3 py-2 font-semibold text-slate-600">Page</th>
          </tr>
        </thead>
        <tbody>
          {reactions.map((rxn, idx) => {
            // Highlight if hovered uuid matches the reaction itself, any reactant, product, or condition text unit
            const isHighlighted = !!(
              hoveredObjectUuid &&
              (rxn.uuid === hoveredObjectUuid ||
                rxn.reactants.some((r) => r.uuid === hoveredObjectUuid) ||
                rxn.products.some((p) => p.uuid === hoveredObjectUuid) ||
                rxn.conditionUuids.includes(hoveredObjectUuid))
            );
            return (
              <tr
                key={rxn.uuid || `rxn-${idx}`}
                ref={(el) => setRowRef(rxn.uuid, el)}
                className={`border-b border-slate-200 transition-colors cursor-pointer align-top ${
                  isHighlighted ? "bg-blue-100" : "hover:bg-blue-50"
                }`}
                onMouseEnter={() => onHoverObject?.(rxn.uuid)}
                onMouseLeave={() => onHoverObject?.(null)}
                onClick={() => {
                  onScrollToPage?.(rxn.pageIndex);
                  setSelectedRxn(rxn);
                }}
              >
                <td className="px-3 py-3 text-xs text-slate-400">{idx + 1}</td>
                <td className="px-3 py-3 overflow-hidden">
                  <MoleculeStack molecules={rxn.reactants} />
                </td>
                <td className="px-3 py-3 overflow-hidden">
                  <MoleculeStack molecules={rxn.products} />
                </td>
                <td className="px-3 py-3 overflow-hidden">
                  {rxn.conditions.length > 0 ? (
                    <div className="flex flex-col gap-1 min-w-0">
                      {rxn.conditions.map((c, ci) => (
                        <span
                          key={ci}
                          className="text-xs text-slate-600 truncate block"
                          title={c}
                        >
                          {c}
                        </span>
                      ))}
                    </div>
                  ) : (
                    <span className="text-xs text-slate-400 italic">None</span>
                  )}
                </td>
                <td className="px-3 py-3 text-xs text-slate-500">
                  {rxn.pageIndex + 1}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      {selectedRxn && (
        <ReactionDetailModal
          reactants={selectedRxn.reactants}
          products={selectedRxn.products}
          conditions={selectedRxn.conditions}
          onClose={() => setSelectedRxn(null)}
        />
      )}
    </>
  );
};

export default ChemistryDisplay;
